import React from "react";
import { Typography, Box, Checkbox, FormControlLabel, TextField, Card, CardContent, Divider, FormControl, InputLabel, Select, MenuItem, Button } from "@mui/material";

// Configuration for lists
const BOOKKEEPING_SERVICES = [
  "Cash Flow Management", "Accounts Payable & Receivable", "Bank Reconciliations", 
  "Payroll", "Superannuation", "Invoicing", "Regular Financial Reporting"
];
const TAXATION_SERVICES = [
  "Preparation of Tax Returns", "BAS", "IAS", "Tax Planning", "Business Advisory"
];
const STRUCTURING_SERVICES = [
  "Business Name Registration", "Company Registration", "Trust Registration", "TFN registration", "ABN Registration", "GST Registration"
];

const DURATION_OPTIONS = [
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "6-Months", label: "6 Months" },
    { value: "Yearly", label: "Yearly" },
    { value: "Once", label: "Once" },
];

const ServiceItem = ({ label, isSelected, price, duration, onToggle, onPriceChange, onDurationChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <FormControlLabel
            control={<Checkbox checked={isSelected} onChange={onToggle} />}
        />
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', flexGrow: 1, fontSize: '1rem' }}>{label}</Typography>
        <FormControl size="small" sx={{ width: 140 }} disabled={!isSelected}>
            <InputLabel>Duration</InputLabel>
            <Select
                value={duration || "Monthly"}
                label="Duration"
                onChange={(e) => onDurationChange(e.target.value)}
            >
                {DURATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
        <TextField 
            label="Price ($)" 
            type="number" 
            size="small" 
            value={(price !== undefined && price !== null) ? price : ""}
            onChange={(e) => onPriceChange(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={!isSelected}
            required={isSelected}
            sx={{ width: 120 }}
            InputProps={{ inputProps: { min: 0 } }}
        />
    </Box>
);

const ServiceGroup = ({ title, items, selectedItems, onItemChange, customItems, onCustomChange }) => {
    // selectedItems is array of { label, price, duration }
    
    const isSelected = (label) => selectedItems.some(i => i.label === label);
    const getPrice = (label) => {
        const item = selectedItems.find(i => i.label === label);
        return item && (item.price !== undefined && item.price !== null) ? item.price : "";
    };
    const getDuration = (label) => selectedItems.find(i => i.label === label)?.duration || "Monthly";

    const handleToggle = (label) => {
        if (isSelected(label)) {
            // Remove
            onItemChange(selectedItems.filter(i => i.label !== label));
        } else {
            // Add
            onItemChange([...selectedItems, { label, price: 0, duration: "Monthly" }]);
        }
    };

    const handlePrice = (label, newPrice) => {
        onItemChange(selectedItems.map(i => i.label === label ? { ...i, price: newPrice } : i));
    };

    const handleDuration = (label, newDuration) => {
        onItemChange(selectedItems.map(i => i.label === label ? { ...i, duration: newDuration } : i));
    };

    // Custom Items Handlers
    const addCustomItem = () => {
        onCustomChange([...(customItems || []), { text: "", price: 0, duration: "Monthly" }]);
    };

    const removeCustomItem = (index) => {
        const newItems = [...(customItems || [])];
        newItems.splice(index, 1);
        onCustomChange(newItems);
    };

    const updateCustomItem = (index, field, value) => {
        const newItems = [...(customItems || [])];
        newItems[index] = { ...newItems[index], [field]: value };
        onCustomChange(newItems);
    };

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{title}</Typography>
                <Divider sx={{ mb: 2 }} />
                
                {items.map(label => (
                    <ServiceItem 
                        key={label}
                        label={label}
                        isSelected={isSelected(label)}
                        price={getPrice(label)}
                        duration={getDuration(label)}
                        onToggle={() => handleToggle(label)}
                        onPriceChange={(val) => handlePrice(label, val)}
                        onDurationChange={(val) => handleDuration(label, val)}
                    />
                ))}

                {/* Custom Other Fields */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>Other / Custom Services:</Typography>
                    
                    {(customItems || []).map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                            <TextField 
                                label="Description" 
                                size="small" 
                                fullWidth 
                                value={item.text || ""}
                                onChange={(e) => updateCustomItem(index, 'text', e.target.value)}
                            />
                            <FormControl size="small" sx={{ width: 140 }}>
                                <InputLabel>Duration</InputLabel>
                                <Select
                                    value={item.duration || "Monthly"}
                                    label="Duration"
                                    onChange={(e) => updateCustomItem(index, 'duration', e.target.value)}
                                >
                                    {DURATION_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField 
                                 label="Price ($)"
                                 type="number"
                                 size="small"
                                 value={(item.price !== undefined && item.price !== null) ? item.price : ""}
                                 onChange={(e) => updateCustomItem(index, 'price', e.target.value === "" ? "" : Number(e.target.value))}
                                 sx={{ width: 120 }}
                                 InputProps={{ inputProps: { min: 0 } }}
                            />
                            <Button 
                                color="error" 
                                size="small" 
                                onClick={() => removeCustomItem(index)}
                                sx={{ minWidth: 40 }}
                            >
                                X
                            </Button>
                        </Box>
                    ))}

                    <Button 
                        variant="text" 
                        size="small" 
                        onClick={addCustomItem}
                        startIcon={<span>+</span>}
                    >
                        Add Item
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};



const StepServicesPricing = ({ data, onChange }) => {
    // data structure: { bookkeeping: { selected: [], others: [] }, taxation: ..., structuring: ... }
    
    // Migration helper: if 'other' (object) exists but 'others' (array) doesn't, migrate it.
    // This is optional but good for safety if we had persistence. 
    // Since we are changing initial state in Wizard, this is less critical but good hygiene.

    const updateGroup = (group, field, value) => {
        onChange({
            ...data,
            [group]: { ...data[group], [field]: value }
        });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>Services & Pricing</Typography>
            
            <ServiceGroup 
                title="Bookkeeping" 
                items={BOOKKEEPING_SERVICES}
                selectedItems={data.bookkeeping?.selected || []}
                onItemChange={(items) => updateGroup('bookkeeping', 'selected', items)}
                customItems={data.bookkeeping?.others || []}
                onCustomChange={(items) => updateGroup('bookkeeping', 'others', items)}
            />

            <ServiceGroup 
                title="Taxation" 
                items={TAXATION_SERVICES}
                selectedItems={data.taxation?.selected || []}
                onItemChange={(items) => updateGroup('taxation', 'selected', items)}
                customItems={data.taxation?.others || []}
                onCustomChange={(items) => updateGroup('taxation', 'others', items)}
            />

            <ServiceGroup 
                title="Business Structuring" 
                items={STRUCTURING_SERVICES}
                selectedItems={data.structuring?.selected || []}
                onItemChange={(items) => updateGroup('structuring', 'selected', items)}
                customItems={data.structuring?.others || []}
                onCustomChange={(items) => updateGroup('structuring', 'others', items)}
            />
        </Box>
    );
};

export default StepServicesPricing;
